import { OtlTextFormField as TextFormField } from "./OtlTextFormField";
import React, { useEffect } from "react";
import { useField, useForm } from "react-final-form";
import { OtlPostalCodeField } from "./OtlPostalCodeField";
import {
  FormFieldWrapper,
  FormFieldWrapperProps,
} from "../common/components/FormFields/FormFieldWrapper";
import { grey } from "@mui/material/colors";
import {
  SelectFormField,
  SelectFormFieldOption,
} from "../common/components/FormFields";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { ListOption } from "../common/api/Interfaces/listOption";

import { CANADIAN_PROVINCES } from "../common/api/provinceApi";

// IDs match the backend ProvinceOptionProvider.
const PROVINCES = CANADIAN_PROVINCES.map(({ id, name }) => ({
  value: id,
  text: name,
}));
const NWT_PROVINCES = PROVINCES.filter(({ value }) => value === 12);
const COUNTRIES = [{ value: "CA", text: "Canada" }];

interface OtlAddressFieldsProps
  extends Omit<FormFieldWrapperProps, "children"> {
  name: string;
  label: string;
  communities?: ListOption[];
  isCommunitiesLoading: boolean;
  required?: boolean;
}

export function OtlAddressFields({
  name,
  label,
  required,
  communities,
  isCommunitiesLoading,
  ...wrapperProps
}: OtlAddressFieldsProps) {
  const { input: community } = useField<string>(`${name}.city`, {
    subscription: { value: true },
  });

  const { input: province } = useField<number | string>(
    `${name}.provinceOrTerritory`,
    {
      subscription: { value: true },
    },
  );
  const form = useForm();
  const outsideIsr = community.value === "Outside ISR";

  useEffect(() => {
    if (community.value && !outsideIsr && String(province.value) !== "12") {
      form.change(`${name}.provinceOrTerritory`, 12);
    }
  }, [community.value, outsideIsr, province.value, form, name]);

  return (
    <FormFieldWrapper {...wrapperProps}>
      {label && <Typography color={grey["700"]}>{label}</Typography>}
      <Grid container spacing={0.5} mt={label ? 0 : undefined}>
        <Grid item xs={6}>
          <TextFormField
            name={`${name}.line1`}
            label="Line 1"
            noIcon
            size="small"
            required={required}
          />
        </Grid>
        <Grid item xs={6}>
          <TextFormField
            name={`${name}.line2`}
            label="Line 2"
            noIcon
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <SelectFormField
            name={`${name}.city`}
            label="Community"
            required={required}
            noIcon
            size="small"
            isLoading={isCommunitiesLoading}
            options={communities?.map(
              (d): SelectFormFieldOption => ({
                value: d.name, //Address.city is expecting a string, not an ID, so we feed it the community name
                text: d.name,
              }),
            )}
          />
        </Grid>
        {outsideIsr && (
          <Grid item xs={6}>
            <TextFormField
              name={`${name}.outsideIsrCity`}
              label="City/community name"
              noIcon
              size="small"
              required
              inputProps={{ maxLength: 190 }}
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <SelectFormField
            name={`${name}.provinceOrTerritory`}
            label="Province/Territory"
            noIcon
            size="small"
            options={outsideIsr ? PROVINCES : NWT_PROVINCES}
            required={required}
          />
        </Grid>
        <Grid item xs={6}>
          <OtlPostalCodeField name={`${name}.postalCode`} required={required} />
        </Grid>
        <Grid item xs={6}>
          <SelectFormField
            name={`${name}.country`}
            label="Country"
            noIcon
            options={COUNTRIES}
            size="small"
            required={required}
          />
        </Grid>
      </Grid>
    </FormFieldWrapper>
  );
}
